import { useState } from 'react'
import { createCustomerNote } from '@/api/customers'
import { useAuthStore } from '@/store/authStore'
import { hasPermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import type { Note } from '@/api/types'

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('sv-SE')
}

interface CustomerNotesCardProps {
  customerId: number
  notes: Note[]
  onNoteAdded: (note: Note) => void
}

const SYSTEM_AUTHOR_LABEL = 'Folkbokföringskontroll'

const UNKNOWN_AUTHOR_LABEL = 'Okänd användare'

// Valideringsstationen är enda källan till anteckningar utan användare.
function getNoteAuthor(note: Note): string {
  if (note.user_id === null) {
    return SYSTEM_AUTHOR_LABEL
  }

  return note.user_display_name ?? UNKNOWN_AUTHOR_LABEL
}

export function CustomerNotesCard({ customerId, notes, onNoteAdded }: CustomerNotesCardProps): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const canWrite = hasPermission(user, 'customers.write')
  const [body, setBody] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const canSubmit = body.trim().length > 0 && !isSaving
  const hasError = errorMessage !== null
  const hasNoNotes = notes.length === 0

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    if (!canSubmit) {
      return
    }

    setIsSaving(true)
    setErrorMessage(null)

    try {
      const note = await createCustomerNote(customerId, body.trim())
      onNoteAdded(note)
      setBody('')
    } catch {
      setErrorMessage('Kunde inte spara anteckningen just nu.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>Anteckningar</CardTitle>
        <CardDescription>Interna anteckningar om kunden.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canWrite && (
          <form
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <Textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Skriv en anteckning..."
            />
            <div className="flex items-center justify-between gap-4">
              {hasError && <p className="text-sm text-destructive">{errorMessage}</p>}
              <Button
                type="submit"
                disabled={!canSubmit}
                className="rounded-none ml-auto"
              >
                Spara anteckning
              </Button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className={`rounded-none border px-4 py-3 ${note.kind === 'change' ? 'border-amber-300 bg-amber-50/50' : 'border-border'}`}
            >
              {note.kind === 'change' && (
                <span className="mb-2 inline-flex items-center bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Ändring</span>
              )}
              <p className="whitespace-pre-wrap">{note.body}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {getNoteAuthor(note)} · {formatDateTime(note.created_at)}
              </p>
            </div>
          ))}
        </div>

        {hasNoNotes && <p className="text-sm text-muted-foreground">Inga anteckningar ännu.</p>}
      </CardContent>
    </Card>
  )
}
